import { TextField, Typography } from "@mui/material";
import { useContext, useState } from "react";
import InfoIcon from "components/SVGs/Info";
import { useTranslation } from "react-i18next";
import Button from "components/Button";
import Loader from "components/Loader";
import { useAddStudentMutation } from "app/services/students";
import { IStudent } from "features/Students";
import useToast from "hooks/useToast";
import { IClass } from "features/Classes";
import { validateProperty } from "./utils/validate";
import { IValidationErrors } from "features/common/Interfaces";
import useDebounce from "hooks/useDebounce";
import { FireBaseContext } from "contexts/FireBase";

const customStyles = {
  root: {
    direction: "rtl",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "580px",
    padding: "20px",
    backgroundColor: "rgb(255, 255, 255)",
    borderRadius: "17px",
    overflowY: "auto",
    overflowX: "hidden",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  note: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    paddingBottom: "20px",
  },
};

interface IProps {
  onSubmit(): void;
  classDetails: IClass | null;
}

interface ILine {
  name: "name";
  value: string;
}

const AddingList = ({ onSubmit, classDetails }: IProps) => {
  const { logEvent } = useContext(FireBaseContext);
  const toast = useToast();
  const [isNotValid, setIsNotValid] = useState(false);
  const [errors, setErrors] = useState<IValidationErrors>({});
  const [records, setRecords] = useState<Partial<IStudent>[]>([]);
  const [addStudent, { isLoading }] = useAddStudentMutation();
  const { starting } = useDebounce<React.ChangeEvent<HTMLTextAreaElement>>();

  const { t, i18n } = useTranslation();
  const createHandler = async () => {
    try {
      const studentsInfo: Partial<IStudent>[] = records.map((rec) => ({
        name: rec.name,
        classId: classDetails?.id,
      }));
      const data = await addStudent(studentsInfo).unwrap();
      if (data._statusCode === 200) {
        toast({
          label: t("success"),
          message: t("Students Added"),
          rtl: i18n.dir() === "rtl",
          type: "success",
          toastId: "studentsAdd",
        });
        onSubmit();
      } else if (data._statusCode === 400) {
        toast({
          label: t("error"),
          message: t("Students Not Added"),
          rtl: i18n.dir() === "rtl",
          type: "error",
          toastId: "studentsAdd",
        });
      } else throw data;
    } catch (error) {
      console.log(error);
    }
    logEvent("ClickOnCreateStudent", { Creation_Type: "List bulk" });
  };

  const changeHandler = starting(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const data: ILine[] = event.target.value
        .split("\n")
        .filter(Boolean)
        .map((line: string) => ({ name: "name", value: line.trim() }));
      setErrors({});
      let errorsObject: IValidationErrors = {};
      data.forEach((line: ILine, index: number) => {
        const errorMessage = validateProperty(line);
        if (errorMessage) errorsObject[index] = errorMessage;
        else delete errorsObject[index];
      });
      setIsNotValid(Object.keys(errorsObject).length > 0);
      setErrors(errorsObject);
      setRecords(data.map((line) => ({ name: line.value })));
    }
  );
  return (
    <>
      <Loader open={isLoading} />
      <Typography
        component="div"
        sx={customStyles.root}
        tabIndex={-1}
        style={{ direction: i18n.dir() }}
      >
        <Typography component="div" sx={customStyles.note}>
          <InfoIcon />
          <Typography sx={{ color: "rgb(105 123 140)", fontSize: "16px" }}>
            {t("Copy and paste student list")}
          </Typography>
        </Typography>

        <TextField
          id="outlined-multiline-static"
          onChange={changeHandler}
          multiline
          rows={5}
          helperText={t(Object.values(errors)[0])}
          error={isNotValid}
          sx={{
            width: "100%",
            direction: i18n.dir(),
            "& > p": { textAlign: i18n.dir() === "ltr" ? "left" : "right" },
          }}
        />
        <Button
          label={t("Create students")}
          action={createHandler}
          styles={{ width: "300px", margin: "35px 0" }}
          disable={isNotValid || isLoading || !records.length}
        />
      </Typography>
    </>
  );
};

export default AddingList;
